import type { IncomeDashboardDetail } from '../api/incomeVerificationDashboardApi';
import { serviceCardAccent, type ServiceCardAccent } from './serviceCardAccent';
import { normalizePayorKey } from './incomeStreamPayorMatch';

export type IncludedIncomeStreamView = {
  payor: string;
  payorKey: string;
  monthlyAmount: number;
  frequency: string | null;
  depositsUsed: number;
  isPrimary: boolean;
  accent: ServiceCardAccent;
};

export type IncomeStreamAccentMap = Map<string, ServiceCardAccent>;

function asRecord(v: unknown): Record<string, unknown> | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  return v as Record<string, unknown>;
}

function asNum(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function asStr(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/** Parse included income streams from admin detail (API field or legacy absence). */
export function parseIncludedIncomeStreamsFromDetail(detail: unknown): IncludedIncomeStreamView[] {
  const rec = asRecord(detail);
  if (!rec) return [];
  const metrics = asRecord(rec.metrics);
  const primaryPayor = asStr(metrics?.primaryPayor);
  const rawStreams = rec.incomeStreams;
  if (!Array.isArray(rawStreams) || rawStreams.length === 0) return [];

  const parsed: IncludedIncomeStreamView[] = [];
  for (const raw of rawStreams) {
    const row = asRecord(raw);
    if (!row) continue;
    const payor = asStr(row.payor);
    if (!payor) continue;
    const payorKey = asStr(row.payorKey) ?? normalizePayorKey(payor);
    parsed.push({
      payor,
      payorKey,
      monthlyAmount: asNum(row.monthlyAmount) ?? 0,
      frequency: asStr(row.frequency),
      depositsUsed: asNum(row.depositsUsed) ?? 0,
      isPrimary: row.isPrimary === true || normalizePayorKey(payor) === normalizePayorKey(primaryPayor),
      accent: serviceCardAccent(payorKey),
    });
  }

  parsed.sort((a, b) => b.monthlyAmount - a.monthlyAmount);
  return parsed;
}

export function buildIncomeStreamAccentMap(
  streams: IncludedIncomeStreamView[],
): IncomeStreamAccentMap {
  const map: IncomeStreamAccentMap = new Map();
  for (const stream of streams) {
    map.set(stream.payorKey, stream.accent);
  }
  return map;
}

export function resolveDepositStreamAccent(
  deposit: Pick<IncomeDashboardDetail['incomeDeposits'][number], 'incomeStreamPayorKey' | 'payorNormalized' | 'payorDisplay'>,
  accentMap: IncomeStreamAccentMap,
): ServiceCardAccent | null {
  const key = deposit.incomeStreamPayorKey?.trim();
  if (key && accentMap.has(key)) return accentMap.get(key)!;
  for (const streamKey of accentMap.keys()) {
    const norm = normalizePayorKey(deposit.payorNormalized);
    const display = normalizePayorKey(deposit.payorDisplay);
    if (norm.includes(streamKey) || streamKey.includes(norm) || display.includes(streamKey) || streamKey.includes(display)) {
      return accentMap.get(streamKey)!;
    }
  }
  return null;
}

export function formatStreamTabLabel(payor: string): string {
  const trimmed = payor.trim();
  if (trimmed.length <= 28) return trimmed;
  return `${trimmed.slice(0, 25)}…`;
}
